import React from 'react';
import { Formik } from 'formik';
import { Col, Row, Tooltip } from 'antd';
import { Form, FormItem, Input, ResetButton, SubmitButton, Select, Radio } from 'formik-antd';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { InfoCircleOutlined } from '@ant-design/icons';
import IntlMessages from '../../../../layout/components/lang/IntlMessages';
import Yup from '../../../../vendor/yup';
import { LOCAL_NOTIFICATIONS_CONTENT_TYPE } from '../FormCreateLocalNotification/FormCreateLocalNotification.const';
import { PrivateComponent } from '../../../../components/HasRights/HasRights';
import { Permissions } from '../../../../const/permissions';
import makeTemplatesQueryParams from '../../../../lib/makeTemplatesQueryParams';
import makeSelectFilterOption from '../../../../lib/makeSelectFilterOption';
import mkTemplatesOptions from '../../../Content/utils/mkTemplatesOptions';
import { useGetTemplatesQuery } from '../../../Template builder/api/templatesApiSlice';



const validationSchema = Yup.object().shape({
  id: Yup.string(),
  title: Yup.string(),
  is_published: Yup.string().nullable(),
  template: Yup.string(),
});

const FormNotificationsFilter = ({
  initialValues,
  showPublishedFilter = true,
  contentType = LOCAL_NOTIFICATIONS_CONTENT_TYPE,
  formErrors = {},
  onSubmit,
  onCancel,
  isSubmitting,
}) => {
  const intl = useIntl();
  const {
    data: { data: templates } = { data: [] },
    isFetching: isFetchingTemplates,
  } = useGetTemplatesQuery({
    queryParams: makeTemplatesQueryParams('is_used', contentType),
  });

  return (
    <Formik
      enableReinitialize
      isSubmitting
      initialErrors={formErrors}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      onReset={onCancel}
    >
      {({ isValid, dirty, values }) => {
        return (
          <Form layout="vertical" >
            <FormItem
              name='id'
              label={
                <Row className='w-full' justify="space-between" align="middle">
                  <IntlMessages id="ui-general-id" />
                  <Tooltip placement="topRight" title={<IntlMessages id="ui-general-filter-id-key-help" />}>
                    <InfoCircleOutlined className="hp-ml-8" />
                  </Tooltip>
                </Row>
              }
            >
              <Input
                name="id"
                placeholder={intl.formatMessage({ id: 'local-notifications-form-id-placeholder' })}
              />
            </FormItem>

            <FormItem
              label={intl.formatMessage({ id: 'ui-general-title' })}
              name='title'
            >
              <Input
                name="title"
                placeholder={intl.formatMessage({ id: 'local-notifications-form-title-placeholder' })}
              />
            </FormItem>

            <PrivateComponent allowedPermissions={[ Permissions.CONSTRUCTORS.TEMPLATES.FILTER ]}>
              <FormItem
                label={intl.formatMessage({ id: 'ui-general-template' })}
                name='template'
              >
                <Select
                  allowClear
                  showSearch
                  loading={isFetchingTemplates}
                  name="template"
                  value={values?.template?.toString() ?? null}
                  options={mkTemplatesOptions(templates)}
                  placeholder={intl.formatMessage({ id: 'ui-general-choose-template' })}
                  filterOption={(input, option) => makeSelectFilterOption(option.label, input)}
                />
              </FormItem>
            </PrivateComponent>
            {showPublishedFilter && (
              <Row gutter={[ 16, 16 ]} justify='start'>
                <Col span={12}>
                  <FormItem
                    label={intl.formatMessage({ id: 'ui-general-published' })}
                    name='is_published'
                  >
                    <Radio.Group
                      size='small'
                      name='is_published'
                      defaultValue={null}
                    >
                      <Radio.Button value={null}>{intl.formatMessage({ id: 'ui-general-all' })}</Radio.Button>
                      <Radio.Button value={1}>{intl.formatMessage({ id: 'ui-general-yes' })}</Radio.Button>
                      <Radio.Button value={0}>{intl.formatMessage({ id: 'ui-general-no' })}</Radio.Button>
                    </Radio.Group>
                  </FormItem>
                </Col>
              </Row>
            )}
            <Row gutter={[ 16, 16 ]} justify='end'>
              <Col>
                <ResetButton disabled={false}>
                  <IntlMessages id='ui-general-reset' />
                </ResetButton>
              </Col>

              <Col>
                <SubmitButton
                  type="primary"
                  loading={isSubmitting}
                  disabled={!isValid || !dirty}
                >
                  <IntlMessages id="ui-general-apply" />
                </SubmitButton>
              </Col>
            </Row>
          </Form>
        );
      }}
    </Formik>
  );
};

FormNotificationsFilter.propTypes = {
  initialValues: PropTypes.object.isRequired,
  showPublishedFilter: PropTypes.bool,
  contentType: PropTypes.string,
  formErrors: PropTypes.object,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
};


export default FormNotificationsFilter;
